import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RefreshCw, AlertTriangle, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * Force a date-range of already-SYNCED shifts back into the Xero sync queue.
 *
 * Use this to repair existing wrongly-dated timesheets (e.g. after the
 * iter56 NZ-timezone fix). The next sync run will create fresh
 * timesheets in Xero. The old Xero timesheet IDs are returned so the
 * admin can reverse the duplicates manually in Xero.
 */
export default function XeroResyncRangePanel({ canSync }) {
  const today = new Date().toISOString().slice(0, 10);
  const lastFortnight = new Date(Date.now() - 14 * 86_400_000)
    .toISOString()
    .slice(0, 10);

  const [start, setStart] = useState(lastFortnight);
  const [end, setEnd] = useState(today);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    if (!reason.trim()) {
      toast.error("Please write a short reason — it goes into the audit log.");
      return;
    }
    if (!confirm(
      `Reset ALL synced shifts between ${start} and ${end} so they're pushed to Xero again on the next sync? ` +
      `You'll need to reverse the previous Xero timesheets manually.`
    )) return;
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/admin/xero/timesheets/resync-range", {
        start_date: start,
        end_date: end,
        reason: reason.trim(),
      });
      setResult(data);
      if (data.reset_count > 0) {
        toast.success(`${data.reset_count} shift(s) queued for re-sync.`);
      } else {
        toast.info("No matching SYNCED shifts found in that range.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Re-sync failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardContent className="p-6 space-y-4">
        <div className="flex items-start gap-2">
          <RefreshCw className="h-5 w-5 text-[#1A2744] mt-0.5" />
          <div>
            <h3 className="font-heading text-lg font-bold text-[#1A2744]">
              Re-sync timesheets to Xero
            </h3>
            <p className="text-xs text-gray-500 mt-1">
              Use this when something in the app changed after a timesheet was
              already pushed to Xero (manual shift edits, or fixing the
              off-by-one date bug from earlier releases). All SYNCED shifts in
              the chosen date range will be reset to PENDING — the next sync
              run will create fresh timesheets in Xero.
            </p>
          </div>
        </div>

        <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900 flex gap-2">
          <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <div>
            <strong>Heads-up:</strong> the previous Xero timesheets are <em>not</em>
            auto-deleted (too risky if they're already Approved). After re-sync
            finishes, open each ID listed below in Xero and reverse it manually
            to avoid duplicate hours on payroll.
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Start date (NZ)</Label>
            <Input
              type="date"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              data-testid="xero-resync-start"
            />
          </div>
          <div>
            <Label className="text-xs">End date (NZ)</Label>
            <Input
              type="date"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              data-testid="xero-resync-end"
            />
          </div>
        </div>

        <div>
          <Label className="text-xs">Reason (audit log)</Label>
          <Textarea
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Repair shifts that synced with wrong date before iter56 fix"
            data-testid="xero-resync-reason"
          />
        </div>

        <Button
          onClick={submit}
          disabled={busy || !canSync}
          className="bg-[#1A2744] text-white"
          data-testid="xero-resync-submit"
        >
          <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${busy ? "animate-spin" : ""}`} />
          {busy ? "Resetting…" : "Re-sync this range"}
        </Button>

        {!canSync && (
          <p className="text-xs text-gray-500 italic">Connect to Xero first.</p>
        )}

        {result && (
          <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-xs space-y-2" data-testid="xero-resync-result">
            <div>
              <strong>{result.reset_count}</strong> shift(s) reset to PENDING.{" "}
              {result.note}
            </div>
            {(result.superseded_xero_refs || []).length > 0 && (
              <div>
                <div className="font-semibold text-emerald-900 mt-1">
                  Reverse these in Xero ({result.superseded_xero_refs.length}):
                </div>
                <ul className="mt-1 space-y-0.5 max-h-40 overflow-y-auto">
                  {result.superseded_xero_refs.map((ref) => (
                    <li key={ref} className="font-mono text-[11px] flex items-center gap-2">
                      <ExternalLink className="h-3 w-3" />
                      <a
                        href={`https://payroll.xero.com/Payroll/Timesheets/Edit?TimesheetID=${ref}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-emerald-800 underline hover:text-emerald-900"
                      >
                        {ref}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
